import Tenant, { ParentTenantInformation } from "@/models/tenant/tenant";
import { PATH_KEYS } from "../services/appServices";
import { useItemsCrud } from "./useItemsCrud";
import { useItemService } from "./useServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getTenantIdFromToken } from "@/utils/auth";
import { useCachedAccessToken } from "@/hooks/useCachedAccessToken";
import { TenantDocumentMetadata } from "@/models/metadata/metadata";
import { restError } from "@/models/client/RestResponse";

const useTenant = () => {
  const getCachedToken = useCachedAccessToken();
  const queryClient = useQueryClient();

  /**** Services ****/
  const tenantService = useItemService((services) => services.tenant);

  const tenantCrud = useItemsCrud<Tenant>(
    [PATH_KEYS.tenant],
    (services) => services.tenant
  );

  /**** Queries & Mutations ****/
  const useQueryPageContent = (enabled: boolean = true) =>
    useQuery<string>({
      queryKey: [PATH_KEYS.tenant, "pageContent"],
      queryFn: async () => {
        //Check if in sessionStorage, if yes then return that
        const tenantId = await getTenantIdFromToken(getCachedToken);
        const storedPageContent = sessionStorage.getItem(`content-${tenantId}`);
        if (storedPageContent && storedPageContent !== "{}")
          return storedPageContent;

        const fetchedPageContent = await tenantService.getPageContent();
        if (fetchedPageContent.success) {
          sessionStorage.setItem(
            `content-${tenantId}`,
            fetchedPageContent.value
          );
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
      enabled,
    });

  const useQueryCurrent = (enabled: boolean = true) =>
    useQuery<Tenant>({
      queryKey: [PATH_KEYS.tenant, "current"],
      queryFn: async () => {
        const fetchedPageContent = await tenantService.getCurrent();
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
      enabled,
    });

  const useQueryGetParentTenant = (
    enabled: boolean = true,
    options?: { queryKey?: unknown[] }
  ) =>
    useQuery<ParentTenantInformation>({
      queryKey: options?.queryKey || [PATH_KEYS.tenant, "parentInformation"],
      queryFn: async () => {
        const response = await tenantService.getParentTenantInformation();
        if (response.success) return response.value;
        else throw restError(response);
      },
      enabled,
    });

  const useQueryGetAvailableSubtenants = () =>
    useQuery<Tenant[]>({
      queryKey: [PATH_KEYS.tenant, "availableSubtenants"],
      queryFn: async () => {
        const response = await tenantService.getAvailableSubtenants();
        if (response.success) return response.value;
        else throw restError(response);
      },
    });

  const addAdminEmail = useMutation<
    string,
    string,
    { email: string; tenantId: string }
  >({
    mutationKey: [PATH_KEYS.tenant, "addAdminEmail"],
    mutationFn: async ({
      email,
      tenantId,
    }: {
      email: string;
      tenantId: string;
    }) => {
      const response = await tenantService.addAdminEmail(email, tenantId);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.tenant] });
    },
  });

  const removeAdminEmail = useMutation<
    string,
    string,
    { email: string; tenantId: string }
  >({
    mutationKey: [PATH_KEYS.tenant, "removeAdminEmail"],
    mutationFn: async ({
      email,
      tenantId,
    }: {
      email: string;
      tenantId: string;
    }) => {
      const response = await tenantService.removeAdminEmail(email, tenantId);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.tenant] });
    },
  });

  const updateTenantMetadata = useMutation<
    Tenant,
    string,
    { tenantId: string; tenantMetadataList: TenantDocumentMetadata[] }
  >({
    mutationKey: [PATH_KEYS.tenant, "updateMetadata"],
    mutationFn: async ({
      tenantId,
      tenantMetadataList,
    }: {
      tenantId: string;
      tenantMetadataList: TenantDocumentMetadata[];
    }) => {
      const response = await tenantService.updateMetadata(
        tenantId,
        tenantMetadataList
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.tenant] });
    },
  });

  const deleteTenantMetadata = useMutation<
    Tenant,
    string,
    { tenantMetadataId: string }
  >({
    mutationKey: [PATH_KEYS.tenant, "deleteMetadata"],
    mutationFn: async ({ tenantMetadataId }: { tenantMetadataId: string }) => {
      const response =
        await tenantService.deleteTenantMetadata(tenantMetadataId);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.tenant] });
    },
  });

  /**** Value ****/
  return {
    ...tenantCrud,
    useQueryPageContent,
    useQueryCurrent,
    useQueryGetParentTenant,
    useQueryGetAvailableSubtenants,
    addAdminEmail,
    removeAdminEmail,
    updateTenantMetadata,
    deleteTenantMetadata,
  };
};

export default useTenant;
